/**
 * Complete authentication flow with username support
 * Combines Firebase Auth with Cloud Functions for username lookup
 */

import {
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  signOutUser,
  getCurrentUser,
  onAuthStateChange,
  ensureUserProfileExists,
  requestPasswordReset
} from './firebase';
// Username lookup functions moved to Firestore
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { userRepository } from './services';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { publicProfilesCollection } from "@/lib/public-profile";
import { noteAccountChange } from './stale-account';
import { readWithRetries } from './read-with-retries';

export interface AuthResult {
  success: boolean;
  user?: any;
  error?: string;
  errorCode?: string;
}

export enum AuthErrorCode {
  USERNAME_NOT_FOUND = 'USERNAME_NOT_FOUND',
  USERNAME_TAKEN = 'USERNAME_TAKEN',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  EMAIL_ALREADY_EXISTS = 'EMAIL_ALREADY_EXISTS',
  WEAK_PASSWORD = 'WEAK_PASSWORD',
  INVALID_EMAIL = 'INVALID_EMAIL',
  ACCOUNT_DISABLED = 'ACCOUNT_DISABLED',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  REGISTRATION_DISABLED = 'REGISTRATION_DISABLED',
  GOOGLE_SIGNIN_NOT_ALLOWED = 'GOOGLE_SIGNIN_NOT_ALLOWED',
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

// Helper function to create standardized error responses
function createAuthError(errorCode: AuthErrorCode, message: string): AuthResult {
  return {
    success: false,
    error: message,
    errorCode
  };
}

// Helper function to map Firebase errors to our error codes
function mapFirebaseError(error: any): AuthResult {
  const errorCode = error.code;
  const errorMessage = error.message;

  switch (errorCode) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return createAuthError(AuthErrorCode.INVALID_CREDENTIALS, 'Invalid username or password');

    case 'auth/email-already-in-use':
      return createAuthError(AuthErrorCode.EMAIL_ALREADY_EXISTS, 'This email is already registered');

    case 'auth/invalid-email':
      return createAuthError(AuthErrorCode.INVALID_EMAIL, 'Please enter a valid email address');

    case 'auth/weak-password':
      return createAuthError(AuthErrorCode.WEAK_PASSWORD, 'Password is too weak. Please use at least 6 characters');

    case 'auth/user-disabled':
      return createAuthError(AuthErrorCode.ACCOUNT_DISABLED, 'This account has been disabled');

    case 'auth/too-many-requests':
      return createAuthError(AuthErrorCode.TOO_MANY_REQUESTS, 'Too many failed attempts. Please try again later');

    case 'auth/operation-not-allowed':
      return createAuthError(AuthErrorCode.REGISTRATION_DISABLED, 'Email registration is currently disabled');

    case 'auth/popup-closed-by-user':
      return createAuthError(AuthErrorCode.GOOGLE_SIGNIN_NOT_ALLOWED, 'Google sign-in was cancelled');

    case 'auth/popup-blocked':
      return createAuthError(AuthErrorCode.GOOGLE_SIGNIN_NOT_ALLOWED, 'Popup was blocked. Please allow popups and try again');

    case 'auth/cancelled-popup-request':
      return createAuthError(AuthErrorCode.GOOGLE_SIGNIN_NOT_ALLOWED, 'Google sign-in was cancelled');

    case 'auth/account-exists-with-different-credential':
      return createAuthError(AuthErrorCode.EMAIL_ALREADY_EXISTS, 'An account already exists with this email using a different sign-in method');

    case 'auth/network-request-failed':
      return createAuthError(AuthErrorCode.NETWORK_ERROR, "We couldn't reach the sign-in service. Check your connection and try again.");

    // Google's sign-in service is down or rate-limited for the whole site:
    // nothing the person typed is wrong. (The code really ends in a dot.)
    case 'auth/the-service-is-currently-unavailable.':
    case 'auth/the-service-is-currently-unavailable':
    case 'auth/quota-exceeded':
    case 'auth/internal-error':
      return createAuthError(AuthErrorCode.NETWORK_ERROR, 'Sign-in is having trouble right now. Please try again in a few minutes.');

    default:
      return createAuthError(AuthErrorCode.UNKNOWN_ERROR, errorMessage || 'An unexpected error occurred');
  }
}

// Login with username or email + password
export async function loginWithUsernameOrEmail(usernameOrEmail: string, password: string): Promise<AuthResult> {
  try {
    const normalizedUsernameOrEmail = usernameOrEmail.trim();
    if (!normalizedUsernameOrEmail) {
      return createAuthError(AuthErrorCode.INVALID_CREDENTIALS, 'Username or email is required');
    }

    // Better email detection - check for both @ and . (more reliable)
    const isEmail =
      normalizedUsernameOrEmail.includes('@') &&
      normalizedUsernameOrEmail.includes('.');
    let email = normalizedUsernameOrEmail;

    // If it doesn't look like an email, treat as username and lookup email.
    // Use the public callable — guests can't read /users directly under the
    // current Firestore rules.
    if (!isEmail) {
      try {
        const callable = httpsCallable<
          { username: string },
          { email: string; uid: string; name?: string }
        >(functions, 'lookupUserByUsername');
        const { data } = await callable({ username: normalizedUsernameOrEmail });
        if (!data?.email) {
          return createAuthError(AuthErrorCode.USERNAME_NOT_FOUND, 'Username not found');
        }
        email = data.email;
      } catch (error: any) {
        // not-found is the expected "no such username" code from the callable
        if (error?.code === 'functions/not-found') {
          return createAuthError(AuthErrorCode.USERNAME_NOT_FOUND, 'Username not found');
        }
        if (error?.code === 'functions/failed-precondition') {
          // Two older accounts share this name apart from capitals.
          return createAuthError(AuthErrorCode.USERNAME_NOT_FOUND, error.message || 'Sign in with your email address.');
        }
        return createAuthError(AuthErrorCode.USERNAME_NOT_FOUND, 'Could not look up username. Please try again.');
      }
    } else {
      // Firebase normalizes emails; use lowercase to avoid subtle input issues.
      email = email.toLowerCase();
    }

    // Sign in with email + password
    const result = await signInWithEmail(email, password);

    // Get or create user profile
    let profile = await userRepository.getUserById(result.user.uid);
    if (!profile) {
      // Create profile if it doesn't exist
      await userRepository.createUser(result.user.uid, {
        email: result.user.email,
        name: result.user.displayName,
        emailVerified: result.user.emailVerified,
        updatedAt: new Date(),
      });
      profile = await userRepository.getUserById(result.user.uid);
    }

    return { success: true, user: { ...result.user, id: result.user.uid, profile } };

  } catch (error: any) {
    return mapFirebaseError(error);
  }
}

// Password reset with username or email support
export async function resetPasswordWithUsernameOrEmail(usernameOrEmail: string): Promise<AuthResult> {
  try {
    // Better email detection - check for both @ and . (more reliable)
    const isEmail = usernameOrEmail.includes('@') && usernameOrEmail.includes('.');
    let email = usernameOrEmail;

    console.log("🔐 Detected as email:", isEmail);

    // If it doesn't look like an email, treat as username and lookup email
    // via the public callable (guests can't read /users directly).
    if (!isEmail) {
      console.log("🔐 Looking up email for username:", usernameOrEmail);
      try {
        const callable = httpsCallable<
          { username: string },
          { email: string; uid: string; name?: string }
        >(functions, 'lookupUserByUsername');
        const { data } = await callable({ username: usernameOrEmail });

        if (!data?.email) {
          console.log("🔐 Username not found in database");
          return createAuthError(AuthErrorCode.USERNAME_NOT_FOUND, 'Username not found');
        }

        email = data.email;

        console.log("🔐 Found user email:", email);
      } catch (error: any) {
        if (error?.code === 'functions/not-found') {
          console.log("🔐 Username not found via callable");
          return createAuthError(AuthErrorCode.USERNAME_NOT_FOUND, 'Username not found');
        }
        console.error("🔐 Error looking up username:", error);
        return createAuthError(AuthErrorCode.USERNAME_NOT_FOUND, 'Username not found');
      }
    }

    // Send password reset email (do not log the email — PII)
    await requestPasswordReset(email);

    return { success: true };

  } catch (error: any) {
    console.error("🔐 Password reset error:", error);
    return mapFirebaseError(error);
  }
}

// Login with Google - Only allows existing verified users
export async function loginWithGoogle(): Promise<AuthResult> {
  try {
    const result = await signInWithGoogle();
    
    // Check if user profile exists and is verified
    const profile = await userRepository.getUserById(result.user.uid);
    if (!profile) {
      // No profile under this uid means no email registration: Firebase keeps
      // one account per email, so an email-registered user signing in with
      // Google arrives under the same uid. (A lookup of users by email is not
      // readable by members anyway -- users/{uid} is owner/admin-only.)
      await signOutUser();
      return createAuthError(
        AuthErrorCode.GOOGLE_SIGNIN_NOT_ALLOWED,
        'Please register with email first. Google sign-in is only for existing email accounts.'
      );
    }
    
    // Now check if email is verified (second condition)
    if (!result.user.emailVerified && !profile.emailVerified) {
      await signOutUser();
      return createAuthError(
        AuthErrorCode.EMAIL_NOT_VERIFIED,
        'Please verify your email address before using Google sign-in.'
      );
    }
    
    return { success: true, user: { ...result.user, id: result.user.uid, profile } };
    
  } catch (error: any) {
    return mapFirebaseError(error);
  }
}

// Register with email + password (username set separately)
export async function registerWithEmail(email: string, password: string, name: string): Promise<AuthResult> {
  try {
    console.log('🔄 AUTH: Starting email registration', { email, name });
    
    const result = await signUpWithEmail(email, password, name);
    console.log('📝 AUTH: Firebase signup result:', result);
    
    // Create basic user profile without username (will be set later)
    console.log('🔄 AUTH: Creating user profile for UID:', result.user.uid);
    await userRepository.updateUser(result.user.uid, {
      uid: result.user.uid,
      email: result.user.email,
      name: name,
      roles: ['USER'],
      initialAuthMethod: 'email',
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    
    const profile = await userRepository.getUserById(result.user.uid);
    console.log('✅ AUTH: User profile created successfully:', profile);
    
    return { success: true, user: { ...result.user, id: result.user.uid, profile } };
    
  } catch (error: any) {
    console.error('❌ AUTH: Registration error:', error);
    return mapFirebaseError(error);
  }
}

// Legacy function with username parameter for backwards compatibility
export async function registerWithEmailAndUsername(
  email: string,
  password: string,
  name: string,
  username: string,
  names?: { firstName: string; lastName: string },
): Promise<AuthResult> {
  try {
    // Username is required
    if (!username || username.trim().length === 0) {
      return createAuthError(AuthErrorCode.USERNAME_NOT_FOUND, 'Username is required');
    }

    // Check availability BEFORE creating the Firebase Auth user — otherwise a
    // "taken" verdict leaves an orphaned Auth user the visitor can't recover.
    // Uses the public `checkUsernameAvailable` callable so guests don't get
    // bitten by Firestore rules requiring auth on /users.
    const availability = await checkUsernameAvailable(username);
    if (availability === 'unknown') {
      return createAuthError(AuthErrorCode.USERNAME_TAKEN, 'Could not verify username availability. Please try again.');
    }
    if (availability === 'taken') {
      return createAuthError(AuthErrorCode.USERNAME_TAKEN, 'Username is already taken');
    }

    const result = await signUpWithEmail(email, password, name);

    // Create user profile. The names typed on the form are written here
    // explicitly: the auth listener may already have created a bare profile
    // (before updateProfile set the display name), and then nothing else
    // filled firstName/lastName -- slow connections registered nameless.
    const [first, ...rest] = name.trim().split(/\s+/);
    await userRepository.updateUser(result.user.uid, {
      uid: result.user.uid,
      email: result.user.email,
      name: name,
      firstName: names?.firstName ?? first ?? '',
      lastName: names?.lastName ?? rest.join(' '),
      username: username, // Username is required
      roles: ['USER'],
      initialAuthMethod: 'email',
      createdAt: new Date(),
      updatedAt: new Date(),
    });

    const profile = await userRepository.getUserById(result.user.uid);

    return { success: true, user: { ...result.user, id: result.user.uid, profile } };

  } catch (error: any) {
    return mapFirebaseError(error);
  }
}

/**
 * Check username availability via the public cloud function. Returns one of:
 *   'available' — definitely free
 *   'taken'     — definitely in use OR fails server-side validation (length/chars)
 *   'unknown'   — couldn't reach the server / unexpected error; do NOT mark as
 *                 taken silently. Caller should bail and ask the user to retry.
 */
async function checkUsernameAvailable(username: string): Promise<'available' | 'taken' | 'unknown'> {
  try {
    const callable = httpsCallable<
      { username: string },
      { available: boolean; reason?: string }
    >(functions, 'checkUsernameAvailable');
    const { data } = await callable({ username });
    return data.available ? 'available' : 'taken';
  } catch (error) {
    console.error('Error checking username availability:', error);
    return 'unknown';
  }
}

// Set username for existing user
export async function setUserUsername(username: string): Promise<AuthResult> {
  console.log('🔄 AUTH: Setting username for user:', username);
  
  const user = getCurrentUser();
  if (!user) {
    console.error('❌ AUTH: No authenticated user found');
    return createAuthError(AuthErrorCode.UNKNOWN_ERROR, 'User not authenticated');
  }
  
  console.log('📝 AUTH: Current user UID:', user.uid);
  
  try {
    // Check if username is already taken
    console.log('🔍 AUTH: Checking username availability:', username);
    // Usernames are public; other people's users docs are not readable.
    const q = query(publicProfilesCollection(), where('username', '==', username));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      console.error('❌ AUTH: Username already taken:', username);
      return createAuthError(AuthErrorCode.USERNAME_TAKEN, 'Username already taken');
    }
    
    console.log('✅ AUTH: Username available, updating profile');
    // Update user profile with username
    await userRepository.updateUser(user.uid, { username });
    
    console.log('✅ AUTH: Username set successfully:', username);
    return { success: true };
    
  } catch (error: any) {
    console.error('❌ AUTH: Error setting username:', error);
    return mapFirebaseError(error);
  }
}

// Logout
export async function logout(): Promise<void> {
  await signOutUser();
}

// Get current authenticated user with profile
export async function getCurrentUserWithProfile() {
  const user = getCurrentUser();
  if (!user) return null;
  
  // Reload the user to get the latest provider data (important after password reset)
  try {
    await user.reload();
    console.log('🔄 User reloaded, provider data:', user.providerData?.map(p => p.providerId));
  } catch (error) {
    console.warn('Failed to reload user:', error);
  }
  
  const profile = await userRepository.getUserById(user.uid);
  return { ...user, id: user.uid, profile };
}

const MAX_SUBSCRIPTION_SYNC_RETRIES = 3;

function isRetryableSubscriptionSyncError(error: any): boolean {
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();

  if (
    code.includes('internal') ||
    code.includes('unavailable') ||
    code.includes('deadline-exceeded') ||
    code.includes('aborted') ||
    code.includes('unknown') ||
    code.includes('failed-precondition')
  ) {
    return true;
  }

  if (
    message.includes('internal') ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('cors') ||
    message.includes('failed to fetch')
  ) {
    return true;
  }

  return false;
}

function notifyAuthSyncWarning(message: string) {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent('auth-sync-warning', {
      detail: { message },
    })
  );
}

// Helper function to sync subscription in background
async function syncUserSubscriptionBackground(userId: string, attempt: number = 1) {
  try {
    const syncSubscription = httpsCallable(functions, 'syncUserSubscription');
    const result = await syncSubscription({ userId });

    if (result.data?.subscription) {
      // Successfully synced subscription data
      console.log('✅ SUBSCRIPTION SYNC: Successfully synced subscription data');
    }
  } catch (error: any) {
    // Handle "user not found" as a normal case (user has no subscription to sync)
    if (error?.code === 'functions/not-found' || error?.message?.includes('User not found')) {
      console.log('ℹ️ SUBSCRIPTION SYNC: User has no subscription to sync (normal case)');
      return;
    }

    const shouldRetry = isRetryableSubscriptionSyncError(error) && attempt < MAX_SUBSCRIPTION_SYNC_RETRIES;

    if (shouldRetry) {
      const retryDelayMs = attempt * 1500;
      console.warn(
        `⚠️ SUBSCRIPTION SYNC: Attempt ${attempt} failed, retrying in ${retryDelayMs}ms (non-blocking)`
      );
      console.warn('Retry error message:', error?.message || 'No message');
      console.warn('Retry error code:', error?.code || 'No code');

      if (attempt === 1) {
        notifyAuthSyncWarning(
          'You are signed in. Subscription status is syncing in the background and will retry automatically.'
        );
      }

      setTimeout(() => {
        void syncUserSubscriptionBackground(userId, attempt + 1);
      }, retryDelayMs);
      return;
    }

    // Don't block login if sync fails, just log the error with full details
    console.warn('⚠️ SUBSCRIPTION SYNC: Background sync failed (non-blocking)');
    console.warn(`Final attempt: ${attempt}/${MAX_SUBSCRIPTION_SYNC_RETRIES}`);
    console.warn('Error message:', error?.message || 'No message');
    console.warn('Error code:', error?.code || 'No code');
    console.warn('Error details:', error?.details || 'No details');
    console.warn('Full error object:', error);

    // Check if it's a CORS error and provide helpful information
    if (error?.message?.includes('CORS') || error?.message?.includes('Access-Control-Allow-Origin')) {
      console.warn('🔧 CORS ISSUE: Firebase Cloud Function needs CORS configuration for localhost development.');
      console.warn('📋 To fix: Add CORS headers to the syncUserSubscription Cloud Function:');
      console.warn('   Access-Control-Allow-Origin: http://localhost:5173');
      console.warn('   Access-Control-Allow-Methods: POST, OPTIONS');
      console.warn('   Access-Control-Allow-Headers: Content-Type, Authorization');
    }
    
    // Check if it's an internal error - likely function deployment or code issue
    if (error?.message === 'internal' || error?.code === 'internal') {
      console.warn('🔧 INTERNAL ERROR: Cloud Function may have deployment issues or runtime errors');
      console.warn('📋 Check Firebase Console > Functions logs for detailed error information');
    }

    notifyAuthSyncWarning(
      'You are signed in, but subscription sync is delayed. Access may take a moment to fully refresh.'
    );
  }
}

// Auth state listener with profile
/**
 * `onSwitch` runs at once when Firebase's account changes (signing in or
 * out, or another tab signing in as someone else), before the new profile
 * loads, with whether an account was signed in before and whether one is
 * now:
 *  - the old account is let go of at once: Firebase already sends the new
 *    credentials, so anything still reading the old account's data is
 *    refused;
 *  - a new account means "loading" until its profile arrives: members'
 *    pages wait rather than treat the person as signed out (after signing
 *    in, the dashboard bounced people back to the sign-in page).
 */
export function onAuthStateChangeWithProfile(
  callback: (user: any) => void,
  onSwitch?: (change: { hadAccount: boolean; hasAccount: boolean }) => void
) {
  let current: string | null | undefined;
  return onAuthStateChange(async (firebaseUser) => {
    const uid = firebaseUser?.uid ?? null;
    if (current !== undefined && uid !== current) {
      if (current) noteAccountChange();
      onSwitch?.({ hadAccount: !!current, hasAccount: !!uid });
    }
    current = uid;
    if (firebaseUser) {
      // Time-limited and retried: members' pages wait on this ("loading"
      // until the profile arrives), so a stalled read must not hold sign-in.
      // undefined: it couldn't be read; null: there is none.
      let profile = await readWithRetries(() => userRepository.getUserById(firebaseUser.uid)).catch(() => undefined);
      // Self-heal: a signed-in account with no users/{uid} (a signup that
      // failed half-way) can't save its profile -- the update is denied.
      // Create the missing document instead of leaving them stuck.
      if (profile === null) {
        try {
          await readWithRetries(() => ensureUserProfileExists(firebaseUser), { attempts: 1, ms: 15000 });
          profile = await readWithRetries(() => userRepository.getUserById(firebaseUser.uid));
        } catch (e) {
          console.error('[auth] could not create missing user profile:', e);
        }
      }
      const userWithProfile = { ...firebaseUser, id: firebaseUser.uid, profile: profile ?? null };
      
      // Debug logging for provider data
      console.log('🔍 Auth State Change - Firebase User Provider Data:', {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        providerData: firebaseUser.providerData?.map((p: any) => ({
          providerId: p.providerId,
          uid: p.uid,
          email: p.email
        })),
        hasPasswordProvider: firebaseUser.providerData?.some((p: any) => p.providerId === 'password')
      });
      
      // A later account change while the profile loaded wins.
      if (current !== firebaseUser.uid) return;

      // Sync subscription in background (non-blocking)
      syncUserSubscriptionBackground(firebaseUser.uid);

      callback(userWithProfile);
    } else {
      callback(null);
    }
  });
}

// Check if user has username set
export async function hasUsername(): Promise<boolean> {
  const user = getCurrentUser();
  if (!user) return false;
  
  const profile = await userRepository.getUserById(user.uid);
  return !!(profile?.username);
}

export { onAuthStateChange };